import React from "react";
import Select from "./Select";
import ws from "@/utils/service";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/utils/cn";

type Props = {
  regionName?: string;
  provinceName?: string;
  municipalityName?: string;
  barangayName?: string;
  onChange: (e: Record<string, any>) => void;
  values: Record<string, any>;
  errors?: Record<string, string | string[]>;
  required?: {
    region?: boolean;
    province?: boolean;
    municipality?: boolean;
    barangay?: boolean;
  };
  containerClassName?: string;
  itemClassName?: string;
};

export const transform = (res: any) => ({
  label: res?.attributes?.name,
  value: res?.attributes?.code
});

const fetchRegions = () => async () => {
  const res = await ws.get("/api/v1/taxpayers/address/regions");
  return (res?.data?.data ?? []).map(transform);
};

const fetchProvince = (regionCode) => async () => {
  const res = await ws.get(`/api/v1/taxpayers/address/provinces?region_code=${regionCode}`);
  return (res?.data?.data ?? []).map(transform);
};

const fetchMunicipality = (provinceCode) => async () => {
  const res = await ws.get(
    `/api/v1/taxpayers/address/municipalities?province_code=${provinceCode}`
  );
  return (res?.data?.data ?? []).map(transform);
};

const fetchBarangay = (municipalityCode) => async () => {
  const res = await ws.get(
    `/api/v1/taxpayers/address/barangays?municipality_code=${municipalityCode}`
  );
  return (res?.data?.data ?? []).map(transform);
};

function Address({
  regionName = "region_code",
  provinceName = "province_code",
  municipalityName,
  barangayName,
  onChange,
  values,
  errors,
  required,
  containerClassName,
  itemClassName
}: Props) {
  const handleChange = (key: string) => (newValue: string) => {
    const updatedValues = { ...values, [key]: newValue };
    if (values[key] === newValue) return;
    if (key === regionName) {
      updatedValues[provinceName] = "";
      updatedValues[municipalityName] = "";
      updatedValues[barangayName] = "";
    }
    if (key === provinceName) {
      updatedValues[municipalityName] = "";
      updatedValues[barangayName] = "";
    }
    if (key === municipalityName) {
      updatedValues[barangayName] = "";
    }
    onChange(updatedValues);
  };

  const regionQuery = useQuery({
    queryKey: ["LOOKUP_REGIONS"],
    queryFn: fetchRegions()
  });
  const provinceQuery = useQuery({
    queryKey: ["LOOKUP_PROVINCE", values?.[regionName]],
    queryFn: fetchProvince(values?.[regionName]),
    enabled: Boolean(values?.[regionName])
  });
  const municipalityQuery = useQuery({
    queryKey: ["LOOKUP_MUNICIPALITY", values?.[provinceName]],
    queryFn: fetchMunicipality(values?.[provinceName]),
    enabled: Boolean(values?.[provinceName])
  });
  const barangayQuery = useQuery({
    queryKey: ["LOOKUP_BARANGAY", values?.[municipalityName]],
    queryFn: fetchBarangay(values?.[municipalityName]),
    enabled: Boolean(values?.[municipalityName]) && Boolean(barangayName)
  });

  return (
    <div className={cn("grid w-full grid-cols-1 gap-2 lg:grid-cols-2", containerClassName)}>
      <div className={cn("mb-2", itemClassName)}>
        <Select
          label="Region"
          onChange={handleChange(regionName)}
          options={regionQuery?.data ?? []}
          value={values?.[regionName]}
          isLoading={regionQuery?.isFetching}
          error={errors?.[regionName]}
          placeholder="Select an item"
          required={required?.region}
        />
      </div>
      <div className={cn("mb-2", itemClassName)}>
        <Select
          label="Province"
          onChange={handleChange(provinceName)}
          options={provinceQuery?.data ?? []}
          value={values?.[provinceName]}
          isLoading={provinceQuery?.isFetching}
          error={errors?.[provinceName]}
          placeholder={!values[regionName] ? "-" : "Select an item"}
          required={required?.province}
          disabled={!values?.[regionName]}
        />
      </div>
      {municipalityName && (
        <div className={cn("mb-2", itemClassName)}>
          <Select
            label="City / Municipality"
            onChange={handleChange(municipalityName)}
            options={municipalityQuery?.data ?? []}
            value={values?.[municipalityName]}
            isLoading={municipalityQuery?.isFetching}
            error={errors?.[municipalityName]}
            placeholder={!values[provinceName] ? "-" : "Select an item"}
            required={required?.municipality}
            disabled={!values?.[provinceName]}
          />
        </div>
      )}
      {barangayName && (
        <div className={cn("mb-2", itemClassName)}>
          <Select
            label="Barangay"
            onChange={handleChange(barangayName)}
            options={barangayQuery?.data ?? []}
            value={values?.[barangayName]}
            isLoading={barangayQuery?.isFetching}
            error={errors?.[barangayName]}
            placeholder={!values[municipalityName] ? "-" : "Select an item"}
            required={required?.barangay}
            disabled={!values?.[municipalityName]}
          />
        </div>
      )}
    </div>
  );
}

export default Address;
